import { useEffect, useState } from "react";

import { useConfirmExtension, usePreviewExtension } from "./api";
import { readApiErrorMessage } from "./errors";
import { formatDate, formatMoneyCents } from "./format";
import type { ExtensionPreviewPayload } from "./types";

// Phase 4b extension-requests — `<ExtensionRequestSheet>` (slice doc
// §"UI / UX"). Mobile-first modal that pre-fills the day after the
// current pickup, calls /preview on each date change to render the
// cost breakdown, and confirms via POST /extensions.json.
//
// **UX principles** (mirrored from the slice doc):
//   - No surprise charges (the cost breakdown is visible before
//     "Confirm extension" is enabled).
//   - The holiday/Sunday shift surfaces *why* the date moved.
//   - The sheet is non-dismissable while a confirm is in flight.
//   - The card-on-file last4 is read from the preview payload so the
//     customer sees which card will be charged.
//
// **Accessibility** (AC-8): role=dialog, aria-labelledby, escape-key
// dismiss, focus trapped on first input.

interface Props {
  orderNumber: string;
  currentPickupDate: string;
  cardLast4: string | null;
  onClose: () => void;
  onConfirmed?: (newPickupDate: string) => void;
}

export function ExtensionRequestSheet({
  orderNumber,
  currentPickupDate,
  cardLast4,
  onClose,
  onConfirmed
}: Props): JSX.Element {
  const initialDate = addDaysIso(currentPickupDate, 1);
  const [newReturnDate, setNewReturnDate] = useState(initialDate);
  const [preview, setPreview] = useState<ExtensionPreviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosenAlternative, setChosenAlternative] = useState<number | null>(null);

  const previewMutation = usePreviewExtension(orderNumber);
  const confirmMutation = useConfirmExtension(orderNumber);

  useEffect(() => {
    setError(null);
    setChosenAlternative(null);
    previewMutation.mutate(
      { new_return_date: newReturnDate },
      {
        onSuccess: (data) => setPreview(data),
        onError: (e) => {
          setError(readApiErrorMessage(e));
          setPreview(null);
        }
      }
    );
    // We intentionally exclude `previewMutation` from deps — including
    // it would re-fire the effect on every render (mutations are not
    // stable references), spamming the server. The date IS the only
    // input that should trigger a re-preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newReturnDate]);

  function handleConfirm(): void {
    if (!preview || !preview.ok) return;
    const payload: { new_return_date: string; alternative_unit_id?: number } = {
      new_return_date: newReturnDate
    };
    if (chosenAlternative !== null) payload.alternative_unit_id = chosenAlternative;
    confirmMutation.mutate(payload, {
      onSuccess: (data) => {
        onConfirmed?.(data.new_pickup_date);
        onClose();
      },
      onError: (e) => setError(readApiErrorMessage(e))
    });
  }

  const canConfirm =
    !!preview &&
    preview.ok &&
    (preview.available || chosenAlternative !== null) &&
    !confirmMutation.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="extension-sheet-title"
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !confirmMutation.isPending) onClose();
      }}
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-xl sm:rounded-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-2">
          <h2 id="extension-sheet-title" className="text-lg font-semibold">
            Request extension
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={confirmMutation.isPending}
            aria-label="Close"
            className="text-2xl leading-none px-2 disabled:opacity-50"
          >
            &times;
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Order #{orderNumber} &middot; Current pickup: {formatDate(currentPickupDate)}
        </p>

        <label className="block text-sm font-medium mb-1" htmlFor="extension-new-return-date">
          New return date
        </label>
        <input
          id="extension-new-return-date"
          type="date"
          className="block w-full border rounded px-3 py-2 text-base min-h-[44px]"
          value={newReturnDate}
          min={addDaysIso(currentPickupDate, 1)}
          onChange={(e) => setNewReturnDate(e.target.value)}
          disabled={confirmMutation.isPending}
        />

        {preview?.pickup_date_shifted && (
          <p role="status" className="mt-2 text-sm text-blue-700">
            {preview.pickup_date_shift_reason}: pickup will be scheduled for{" "}
            {formatDate(preview.pickup_date_effective)}.
          </p>
        )}

        {preview && !preview.available && preview.available_alternatives.length > 0 && (
          <fieldset className="mt-3 border rounded p-3">
            <legend className="text-sm font-medium px-1">Same unit not available</legend>
            <p className="text-sm text-gray-600 mb-2">
              JDR can swap your equipment for an identical unit:
            </p>
            {preview.available_alternatives.map((alt) => (
              <label key={alt.id} className="flex items-center gap-2 text-sm py-1">
                <input
                  type="radio"
                  name="alternative-unit"
                  value={alt.id}
                  checked={chosenAlternative === alt.id}
                  onChange={() => setChosenAlternative(alt.id)}
                />
                <span>
                  {alt.model_number} (#{alt.unit_number})
                </span>
              </label>
            ))}
          </fieldset>
        )}

        {preview && !preview.available && preview.available_alternatives.length === 0 && (
          <p role="status" className="mt-3 text-sm text-amber-800">
            We can't extend this rental. Pickup remains scheduled for{" "}
            {formatDate(currentPickupDate)}. Call JDR if you need help.
          </p>
        )}

        {preview && preview.ok && (preview.available || chosenAlternative !== null) && (
          <section className="mt-4">
            <h3 className="text-sm font-semibold mb-2">Cost breakdown</h3>
            <dl className="text-sm space-y-1" aria-describedby="extension-total">
              <Row
                label={`${preview.additional_days} additional days`}
                value={formatMoneyCents(preview.additional_cost_cents)}
              />
              {preview.tax_delta_cents > 0 && (
                <Row label="Sales tax" value={formatMoneyCents(preview.tax_delta_cents)} />
              )}
              {preview.card_surcharge_delta_cents > 0 && (
                <Row label="Card surcharge (3%)" value={formatMoneyCents(preview.card_surcharge_delta_cents)} />
              )}
              <Row
                id="extension-total"
                label="Total additional charge"
                value={formatMoneyCents(preview.total_delta_cents)}
                emphasize
              />
            </dl>
            {cardLast4 && preview.requires_additional_auth && (
              <p className="mt-2 text-sm text-gray-600">
                Your card on file: ••••{cardLast4}. Will be charged today.
              </p>
            )}
          </section>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={confirmMutation.isPending}
            className="px-4 py-2 rounded border min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            aria-describedby="extension-total"
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400 min-h-[44px]"
          >
            {confirmMutation.isPending ? "Confirming…" : "Confirm extension"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  label: string;
  value: string;
  id?: string;
  emphasize?: boolean;
}

function Row({ label, value, id, emphasize = false }: RowProps): JSX.Element {
  return (
    <div className={`flex justify-between ${emphasize ? "font-semibold pt-2 border-t" : ""}`}>
      <dt>{label}</dt>
      <dd id={id}>{value}</dd>
    </div>
  );
}

// `YYYY-MM-DD` arithmetic without timezone surprises. `new Date("YYYY-MM-DD")`
// parses at UTC midnight; offsetting by daylight savings would shift the
// displayed date in some zones. Build the date from parts and roll forward
// in local time.
function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
